// @ts-strict-ignore
import * as React from 'react';
import isEqual from 'lodash/isEqual';
import clsx from 'clsx';

/**
 * Multi events menu: selectable list of events/custom events to go inside
 * mp-drop-menu
 */
import {useStore} from 'common/util/component-helpers';
import {ButtonBarReact} from 'common/components/button-bar';
import {ItemsMenuReact} from 'common/components/items-menu';
import {propagateEvent} from 'common/util/dom';
import {ContextPanelReact} from 'common/widgets/context-panel';
import {useEventsController} from 'common/contexts/react/events-controller-context';
import {useLocalStorageManager} from 'common/contexts/react/storage-manager-context';
import {useTranslation} from 'common/i18n/hooks';

import {EventsMenu, EventsMenuProps} from '../events-menu';
import {TRACKING_EVENTS} from '../constants';
import {MenuSearchBar} from '../mixins/search-bar';

const NON_HIDDEN_SECTION = 0;
const HIDDEN_SECTION = 1;

export class MultiEventsMenu extends EventsMenu {
  state = {
    eventData: [],
    includeHidden: false,
    searchFilter: ``,
    isSidebarTagSectionExpanded: false,
    selectedTagSet: new Set<string>(),
    sections: [],
    isMenuLoading: false,
    stagedSelected: new Set(),
  };

  declare _onStoreCallback: () => void;

  canCommit() {
    return this.state.stagedSelected.size !== this.getEventItemsFromContext().length;
  }

  componentDidUpdate(prevProps: Readonly<EventsMenuProps>): void {
    if (this.props) {
      this.setOpenedAt(this.props.open);
    }

    if (this.props && this.props.open !== prevProps.open) {
      this.setState({
        includeHidden: false,
        selectedTagSet: new Set(),
        searchFilter: ``,
      });

      if (this.props.open) {
        this.trackEvent(TRACKING_EVENTS.OPEN_EVENTS_DROPDOWN);
        this.updateEventItems();
      } else {
        this.resetStagedSelection(); // no-op if component is not connected
      }
    } else if (
      this.isConnected &&
      (this.props.inverted !== prevProps.inverted ||
        !isEqual(this.props.selected, prevProps.selected) ||
        this.props.includeHidden !== prevProps.includeHidden)
    ) {
      this.handleSelectionChange();
    }

    this.props.onRender?.();
  }

  componentDidMount() {
    this.isConnected = true;
    this.resetStagedSelection();

    this._onStoreCallback = () => this.handleSelectionChange();
    this.props.eventsController.providerStores.forEach((store) => useStore(this, store, this._onStoreCallback));
  }

  updateEventItems() {
    this.handleSelectionChange();
  }

  getEventItemsFromContext() {
    const sections = this.getSectionsFromContext();
    return this.getItemsFromSections(sections);
  }

  getSectionsFromContext() {
    const sections = this.props.eventsController.getSections({
      dataGroupIds: this.props.dataGroupIds,
      excludeAutotrackEvents: this.props.excludeAutotrackEvents,
      excludeCustomEvents: this.props.excludeCustomEvents,
    });

    if (this.props.includeHidden) {
      return sections;
    } else {
      return [sections[0]];
    }
  }

  get eventData() {
    return this.getEventItemsFromContext().map((sectionItem) => sectionItem.value);
  }

  handleSelectAll(ev) {
    const {selectionState} = ev.detail;
    const {sections} = this.state;
    const selectedItems = selectionState === `all` ? [] : this.eventData.map((event) => event.value);

    this.setState({stagedSelected: new Set(selectedItems)});
    this.updateSections(sections);
  }

  handleSelectAllMatching(ev) {
    const {sections, stagedSelected} = this.state;
    const matchingSections = ev.detail.matchingSections;

    const allMatchingEventsSelected = !matchingSections.some((section) =>
      section.items.some(({value: eventItem}) => !stagedSelected.has(eventItem.value)),
    );

    const updatedStagedSelected = new Set(stagedSelected);
    matchingSections.forEach((section) =>
      section.items.forEach(({value: eventItem}) =>
        allMatchingEventsSelected
          ? updatedStagedSelected.delete(eventItem.value)
          : updatedStagedSelected.add(eventItem.value),
      ),
    );

    this.setState({stagedSelected: updatedStagedSelected});
    this.updateSections(sections);
  }

  onCommitClick() {
    this.dispatchSelectedEvent();

    const selectionDetail = this.getSelectionDetail();
    this.trackSelection({
      eventName: `Hide events`,
      props: {
        action: selectionDetail.action,
        '# of hidden events': selectionDetail.selectedCount,
        '# of visible events': selectionDetail.unselectedCount,
      },
    });
  }

  handleItemClick(item) {
    const {sections} = this.state;

    this.updateSelectedValues(item.value);
    this.updateSections(sections);
  }

  handleSelectionChange() {
    this.resetStagedSelection();
  }

  // when 'inverted' is set, the list takes
  // all events as being selected excluding the
  // ones not in `selected`
  // WARNING: no-op if component is not connected
  resetStagedSelection() {
    // Contexts are only available after the component is connected to the DOM
    if (this.isConnected) {
      let selected = this.props.selected;
      if (this.props.inverted) {
        // @ts-ignore
        selected = this.eventData.filter((event) => !selected.has(event.value)).map((event) => event.value);
      }

      this.setState({stagedSelected: new Set(selected)});
      this.updateSections(this.getSectionsFromContext(), true);
    }
  }

  getSections() {
    if (this.props.eventsController.loading) {
      return [
        {
          isLoading: true,
          items: [],
        },
      ];
    }

    return this.state.sections;
  }

  getItemsFromSections(sections) {
    return sections.reduce((items, section) => items.concat(section.items), []);
  }

  // re-organizes current menu items by sorting
  // them by selection state
  updateSections(sections, reorderItems = false) {
    this.setState((prevState) => {
      const updatedSections = sections.map((section) => ({
        ...section,
        items: section.items.map((item) => ({
          ...item,
          isSelected: prevState.stagedSelected.has(item.value.value),
        })),
      }));

      if (reorderItems) {
        const selected = this.getItemsFromSections(updatedSections).filter((item) => item.isSelected);
        const nonHidden = updatedSections[NON_HIDDEN_SECTION].items.filter((item) => !item.isSelected);
        const hidden = updatedSections[HIDDEN_SECTION]?.items.filter((item) => !item.isSelected);

        if (selected.length <= nonHidden.length + (hidden?.length || 0)) {
          updatedSections[NON_HIDDEN_SECTION].items = selected.concat(nonHidden);
          if (hidden) {
            updatedSections[HIDDEN_SECTION].items = hidden;
          }
        } else {
          updatedSections[NON_HIDDEN_SECTION].items = nonHidden.concat(selected);
          if (hidden) {
            updatedSections[HIDDEN_SECTION].items = hidden;
          }
        }
      }
      return {sections: updatedSections};
    });
  }

  // store selectedLabel in state as it must
  // be deferred to commited selection changes
  selectedLabel() {
    let suffix = ``;

    const selectedCount = this.props.selected.size;
    const {t} = this.props.i18n;

    if (this.props.inverted) {
      suffix = ` · ${t(`query-builder.hide-events-all-but`, {count: selectedCount})}`;
    } else if (selectedCount > 0) {
      suffix = ` · ${selectedCount}`;
    }

    return `${t(`query-builder.hide-events`)}${suffix}`;
  }

  updateSelectedValues(eventItem) {
    const stagedSelected = new Set(this.state.stagedSelected);
    const value = eventItem.value;

    if (stagedSelected.has(value)) {
      stagedSelected.delete(value);
    } else {
      stagedSelected.add(value);
    }

    this.setState({stagedSelected});
  }

  dispatchSelectedEvent() {
    const stagedSelected = this.state.stagedSelected;
    const selectionDetail = this.getSelectionDetail();

    this.props.onSelect({
      detail: {
        ...selectionDetail,
        selected: Array.from(stagedSelected),
        unselected: this.eventData.filter((item) => !stagedSelected.has(item.value)).map((item) => item.value),
      },
    });
  }

  getCommitLabel() {
    const {selectedCount} = this.getSelectionDetail();
    const {t} = this.props.i18n;

    if (selectedCount === 0) {
      return t(`common.apply`);
    }

    return t(`query-builder.hide-x-events`, {count: selectedCount});
  }

  getSelectionDetail() {
    const {stagedSelected} = this.state;
    const selectedCount = stagedSelected.size;
    const unselectedCount = this.getEventItemsFromContext().length - selectedCount;
    let action = `hide`;

    // when selected items take most of the list
    // users are just showing a subset of events
    if (unselectedCount && unselectedCount < selectedCount) {
      action = `show`;
    }

    return {
      action,
      selectedCount,
      unselectedCount,
    };
  }

  render() {
    if (!this.props.open) {
      return <div className={clsx(`mp-multi-events-menu`, `mp-child-menu`)} />;
    }

    const {searchFilter} = this.state;

    return (
      <div ref={this.childMenuRef} className={clsx(`mp-multi-events-menu`, `mp-child-menu`)}>
        <MenuSearchBar
          ref={(el) => this.props.onSetKeydownProxyEl(el)}
          value={this.state.searchFilter}
          handleSearchInput={(searchFilterString) => this.setState({searchFilter: searchFilterString})}
        />
        <ItemsMenuReact
          elref={`mainSelectionMenu`}
          showSelectAll={!this.state.isMenuLoading}
          showSelectAllMatching={!this.state.isMenuLoading}
          multiple={this.props.multiple}
          searchFilter={searchFilter}
          onClick={(ev) => ev.stopPropagation()}
          onClickItem={(ev: {detail: {item: any}}) => this.handleItemClick(ev.detail.item)}
          onSelectAll={(ev) => this.handleSelectAll(ev)}
          onSelectAllMatching={(ev) => this.handleSelectAllMatching(ev)}
          onFocusItem={(ev) => this.handleItemFocus(ev)}
          keydownProxyEl={this.props.keydownProxyEl}
          sections={this.getMenuSections()}
        />
        {this.props.open && this.props.tooltipData ? (
          <ContextPanelReact
            data={this.props.tooltipData}
            elref={`contextPanel`}
            onTooltipContextAction={(ev) => propagateEvent(this, ev)}
            onTooltipContextEditDescription={(ev) => propagateEvent(this, ev)}
          />
        ) : null}
        <ButtonBarReact
          className='mp-button-bar-primary'
          disabled={!this.canCommit()}
          onClick={() => this.onCommitClick()}
        >
          {this.getCommitLabel()}
        </ButtonBarReact>
      </div>
    );
  }
}

export const MultiEventsMenuReact = React.forwardRef<MultiEventsMenu, EventsMenuProps>((props, ref) => {
  const eventsController = useEventsController();
  const localStorageManager = useLocalStorageManager();
  const i18n = useTranslation();

  return (
    <MultiEventsMenu
      ref={ref}
      eventsController={eventsController}
      localStorageManager={localStorageManager}
      i18n={i18n}
      {...props}
    />
  );
});
